import type {
  AuditEntry,
  CreateInviteInput,
  GuestAction,
  GuestContext,
  House,
  HouseInfo,
  Invite,
  ScheduleKind,
  ScheduleSettings,
  SetDefaultsInput,
  SetScheduleInput,
} from '@guest/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      if (body.message) message = body.message;
      code = body.error;
    } catch {
      /* non-JSON error body; keep the generic message */
    }
    throw new ApiError(message, res.status, code);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

// ------------------------------------------------------------------- guest

export const guestApi = {
  redeem: (code: string) => post<{ ok: true }>('/api/guest/redeem', { code }),
  signOut: () => post<{ ok: true }>('/api/guest/signout'),
  context: () => request<GuestContext>('/api/guest/context'),
  act: (action: GuestAction) => post<{ ok: true }>('/api/guest/action', action),
  info: () => request<HouseInfo>('/api/guest/info'),
  schedule: () => request<ScheduleSettings>('/api/guest/schedule'),
  setSchedule: (input: SetScheduleInput) =>
    request<ScheduleSettings>('/api/guest/schedule', { method: 'PUT', body: JSON.stringify(input) }),
  clearSchedule: (kind: ScheduleKind) =>
    request<ScheduleSettings>('/api/guest/schedule', {
      method: 'DELETE',
      body: JSON.stringify({ kind }),
    }),
};

// ------------------------------------------------------------------- admin

export interface AdminHouse {
  house: House;
  haConnected: boolean;
}

export const adminApi = {
  session: () => request<{ signedIn: boolean }>('/api/admin/session'),
  login: (password: string) => post<{ ok: true }>('/api/admin/login', { password }),
  logout: () => post<{ ok: true }>('/api/admin/logout'),
  house: () => request<AdminHouse>('/api/admin/house'),
  invites: () => request<{ invites: Invite[] }>('/api/admin/invites'),
  invite: (id: string) => request<{ invite: Invite; audit: AuditEntry[] }>(`/api/admin/invites/${id}`),
  create: (input: CreateInviteInput) => post<{ invite: Invite }>('/api/admin/invites', input),
  update: (id: string, input: Partial<CreateInviteInput>) =>
    request<{ invite: Invite }>(`/api/admin/invites/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  revoke: (id: string) => post<{ invite: Invite }>(`/api/admin/invites/${id}/revoke`),
  unrevoke: (id: string) => post<{ invite: Invite }>(`/api/admin/invites/${id}/unrevoke`),
  regenerate: (id: string) => post<{ invite: Invite }>(`/api/admin/invites/${id}/regenerate`),
  remove: (id: string) => request<{ ok: true }>(`/api/admin/invites/${id}`, { method: 'DELETE' }),
  houseInfo: () => request<HouseInfo>('/api/admin/house-info'),
  scheduleDefaults: () => request<SetDefaultsInput>('/api/admin/schedule-defaults'),
  saveScheduleDefaults: (input: SetDefaultsInput) =>
    request<SetDefaultsInput>('/api/admin/schedule-defaults', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  saveHouseInfo: (info: HouseInfo) =>
    request<HouseInfo>('/api/admin/house-info', { method: 'PUT', body: JSON.stringify(info) }),
  audit: (limit = 250) => request<{ entries: AuditEntry[] }>(`/api/admin/audit?limit=${limit}`),
};
